// apps/provider/src/components/ai/ScribePanel.jsx
// Multi-step floating scribe: template selection → recording → transcribe → insert

import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  X, Copy, Download, FileText, ChevronDown, ChevronRight,
  Loader2, AlertCircle, Edit3, Mic, Check, Clock, Layers, Save
} from 'lucide-react';
import { useScribeStore } from '../../stores/scribeStore';
import { apiService } from '@/services/api';
import { aiService } from '@/services/aiService';
import { Button } from '../ui/Button';
import { cn } from '@/lib/utils';
import { useUser } from '@/contexts/UserContext';
import { useCurrentPatient } from '@/contexts/CurrentPatientContext';
import { formatPatientName } from '@/utils/patientName';
import { usePatientNameFormat } from '@/hooks/usePatientNameFormat';
import RecordingControls from '@/components/scribe/RecordingControls';
import { NoteReviewCard } from '@/components/scribe/NoteReviewCard';

// Standard SOAP sentinel — used when user picks the legacy flow
const SOAP_OPTION = { id: '__soap__', name: 'Standard SOAP Note' };

/** Format milliseconds as mm:ss */
const formatDuration = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export function ScribePanel({ onClose }) {
  const location = useLocation();
  const { user } = useUser();
  const { currentPatient } = useCurrentPatient();
  const nameFormat = usePatientNameFormat();

  // Local UI state
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showGeneratedNote, setShowGeneratedNote] = useState(false);
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState('');
  const [savingTranscript, setSavingTranscript] = useState(false);
  const templateDropdownRef = useRef(null);

  // Local SOAP-mode recording state (for SOAP without encounter context)
  const [soapAudioBlob, setSoapAudioBlob] = useState(null);
  const [soapRecording, setSoapRecording] = useState(false);
  const [soapDuration, setSoapDuration] = useState(0);
  const soapMediaRecorderRef = useRef(null);
  const soapChunksRef = useRef([]);
  const soapStartTimeRef = useRef(null);
  const soapStatusIntervalRef = useRef(null);
  const soapStreamRef = useRef(null);

  const {
    // Template
    selectedTemplate,
    templates,
    isLoadingTemplates,
    templateError,
    activeNoteContext,
    loadTemplates,
    selectTemplate,
    // Recording
    isRecording,
    isPaused,
    recordingDuration,
    wakeLockActive,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    // Encounter
    encounterInitialized,
    recordingId,
    hasRecordedAudio,
    existingRecordingState,
    loadExistingRecording,
    isRecordingLocked,
    isDocumentationPopulated,
    hasActiveRecordingForOtherEncounter,
    // Transcription
    transcript,
    isTranscribing,
    transcriptionError,
    transcribeAudio,
    saveTranscriptEdit,
    // SOAP generation
    generatedNote,
    isGeneratingNote,
    noteGenerationError,
    generateSOAPNote,
    // Template generation
    isProcessing,
    processingStatus,
    generatedSectionsData,
    transcribeAndGenerate,
    // Misc
    clearTranscript,
    clearGeneratedNote,
  } = useScribeStore();

  const recordingConflict = hasActiveRecordingForOtherEncounter(activeNoteContext?.encounterId);

  // Load templates on mount
  useEffect(() => {
    if (user?.organization_id && user?.id) {
      loadTemplates(user.organization_id, user.id);
    }
  }, [user?.organization_id, user?.id]);

  // Auto-select template from active note context
  useEffect(() => {
    if (activeNoteContext?.template && !selectedTemplate) {
      selectTemplate(activeNoteContext.template);
    }
  }, [activeNoteContext]);

  // Early encounter initialization — load existing recording state when panel opens
  useEffect(() => {
    if (activeNoteContext?.encounterId && !encounterInitialized && !recordingConflict) {
      loadExistingRecording(activeNoteContext.encounterId);
    }
  }, [activeNoteContext?.encounterId, recordingConflict]);

  // Sync edited transcript
  useEffect(() => {
    setEditedTranscript(transcript);
  }, [transcript]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setShowTemplateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cleanup local SOAP recording on unmount
  useEffect(() => {
    return () => {
      if (soapStatusIntervalRef.current) clearInterval(soapStatusIntervalRef.current);
      if (soapStreamRef.current) soapStreamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // Determine context
  const isInEncounterContext = location.pathname.includes('/encounters');
  const isInClinicalNoteContext = location.pathname.includes('/clinical-notes');
  const canInsertNote = isInEncounterContext || isInClinicalNoteContext;
  const isTemplateMode = selectedTemplate && selectedTemplate.id !== '__soap__';
  const isSoapMode = selectedTemplate?.id === '__soap__';

  // Determine if we have recorded audio (encounter-backed or local SOAP)
  const hasAudio = isTemplateMode
    ? (hasRecordedAudio() || existingRecordingState?.hasAudio)
    : !!soapAudioBlob;
  const activeRecording = isTemplateMode ? isRecording : soapRecording;
  const activeDuration = isTemplateMode ? recordingDuration : soapDuration;
  const hasResults = isTemplateMode ? !!generatedSectionsData : !!generatedNote;
  const locked = isRecordingLocked();
  const populated = isDocumentationPopulated();

  // Dynamic button text
  const getRecordButtonText = () => {
    if (locked) return 'Recording locked';
    if (existingRecordingState?.hasAudio || hasRecordedAudio() || soapAudioBlob) {
      return 'Add to Recording';
    }
    return 'Start Recording';
  };

  // Whether the Transcribe button should be shown
  const showTranscribeButton = hasAudio && !activeRecording && !locked && !isProcessing && !isTranscribing;

  // ============ SOAP Local Recording (no encounter needed) ============

  const startSoapLocalRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      soapStreamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64000 });
      soapMediaRecorderRef.current = recorder;
      soapChunksRef.current = [];
      soapStartTimeRef.current = Date.now();

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) soapChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(soapChunksRef.current, { type: mimeType });
        setSoapAudioBlob(blob);
        setSoapRecording(false);
        if (soapStatusIntervalRef.current) {
          clearInterval(soapStatusIntervalRef.current);
          soapStatusIntervalRef.current = null;
        }
        if (soapStreamRef.current) {
          soapStreamRef.current.getTracks().forEach(t => t.stop());
          soapStreamRef.current = null;
        }
      };

      recorder.start(1000);
      setSoapRecording(true);
      setSoapAudioBlob(null);
      setSoapDuration(0);

      soapStatusIntervalRef.current = setInterval(() => {
        setSoapDuration(Date.now() - soapStartTimeRef.current);
      }, 100);
    } catch (err) {
      console.error('[ScribePanel] SOAP local recording failed:', err);
    }
  }, []);

  const stopSoapLocalRecording = useCallback(() => {
    if (soapMediaRecorderRef.current && soapMediaRecorderRef.current.state !== 'inactive') {
      soapMediaRecorderRef.current.stop();
    }
  }, []);

  // ============ Handlers ============

  const handleTemplateSelect = (template) => {
    selectTemplate(template);
    setShowTemplateDropdown(false);
  };

  const handleStartRecording = async () => {
    if (isTemplateMode) {
      const result = await startRecording();
      if (!result.success && result.error === 'Open a clinical note to use template mode') {
        console.warn('[ScribePanel]', result.error);
      }
    } else {
      // SOAP mode — try encounter-backed first, fall back to local
      const result = await startRecording();
      if (!result.success && result.error === 'NO_ENCOUNTER_CONTEXT') {
        await startSoapLocalRecording();
      }
    }
  };

  const handleStopRecording = async () => {
    if (isTemplateMode || isRecording) {
      await stopRecording();
      // No auto-transcription — user clicks "Transcribe" when ready
    } else if (soapRecording) {
      stopSoapLocalRecording();
      // No auto-transcription for SOAP either — user clicks "Transcribe"
    }
  };

  const handleTranscribe = async () => {
    if (isTemplateMode) {
      const encounterId = activeNoteContext?.encounterId || null;
      try {
        await transcribeAndGenerate(encounterId);
      } catch (err) {
        console.error('[ScribePanel] Transcribe failed:', err);
      }
    } else if (isSoapMode && soapAudioBlob) {
      // SOAP mode: transcribe audio, then user can generate note
      try {
        await transcribeAudio(soapAudioBlob);
      } catch (err) {
        console.error('[ScribePanel] SOAP transcribe failed:', err);
      }
    }
  };

  const handleGenerate = async () => {
    if (isTemplateMode) {
      const encounterId = activeNoteContext?.encounterId || null;
      await transcribeAndGenerate(encounterId);
      setShowGeneratedNote(true);
    } else {
      // Legacy SOAP flow
      const patientContext = {
        patientName: currentPatient ? `${currentPatient.first_name} ${currentPatient.last_name}` : null,
        patientDOB: currentPatient?.birthday,
      };
      await generateSOAPNote(patientContext);
      setShowGeneratedNote(true);
    }
  };

  const handleInsertIntoNote = () => {
    let notePayload;
    if (isTemplateMode && generatedSectionsData) {
      notePayload = {
        sections: generatedSectionsData.sections,
        icd10_suggestions: generatedSectionsData.icd10_suggestions || [],
      };
    } else if (generatedNote) {
      notePayload = generatedNote;
    }
    if (!notePayload) return;

    window.dispatchEvent(new CustomEvent('scribe:insertNote', {
      detail: { note: notePayload }
    }));
    onClose();
  };

  // Replace the note's CPT entry rows with the recommended codes (from the
  // NoteReviewCard). The editor resolves charges live and copies the rows to
  // cpt_codes on save. Returns false when the template has no CPT field.
  const handleApplyCodes = (review) => {
    if (!review?.recommended_codes?.length) return false;
    let target = null;
    for (const section of activeNoteContext?.template?.sections || []) {
      const field = (section.fields || []).find(f => f.type === 'cpt_code_entry');
      if (field) {
        target = { sectionId: section.id, fieldId: field.id };
        break;
      }
    }
    if (!target) return false;
    const rows = review.recommended_codes.map(code => ({
      code: code.code,
      description: code.description || '',
      units: code.units || 1,
      charge: 0,
      modifiers: code.modifiers || [],
      ...(code.minutes != null ? { minutes: code.minutes } : {}),
    }));
    window.dispatchEvent(new CustomEvent('scribe:insertNote', {
      detail: {
        note: { sections: [{ sectionId: target.sectionId, fields: [{ fieldId: target.fieldId, value: rows }] }] },
        replaceFields: true,
      }
    }));
    return true;
  };

  const handleCopyTranscript = () => {
    navigator.clipboard.writeText(isEditingTranscript ? editedTranscript : transcript);
  };

  const handleSaveTranscript = async () => {
    setSavingTranscript(true);
    try {
      await saveTranscriptEdit(editedTranscript);
      setIsEditingTranscript(false);
    } catch (err) {
      console.error('[ScribePanel] Failed to save transcript:', err);
    } finally {
      setSavingTranscript(false);
    }
  };

  const handleCancelTranscriptEdit = () => {
    setEditedTranscript(transcript);
    setIsEditingTranscript(false);
  };

  // ============ Sub-renders ============

  /** Status badge for transcription state */
  const renderStatusBadge = () => {
    const status = existingRecordingState?.transcriptionStatus;
    if (!status || status === 'pending') return null;

    if (status === 'processing') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <Loader2 className="h-3 w-3 animate-spin" />
          Transcribing...
        </span>
      );
    }
    if (status === 'transcribed') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <Loader2 className="h-3 w-3 animate-spin" />
          Generating documentation...
        </span>
      );
    }
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <Check className="h-3 w-3" />
          Documentation populated
        </span>
      );
    }
    if (status === 'failed') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <AlertCircle className="h-3 w-3" />
          Transcription failed
        </span>
      );
    }
    return null;
  };

  /** Recording info display (duration, segments, status) */
  const renderRecordingInfo = () => {
    if (!existingRecordingState) return null;

    return (
      <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-600">
        <span className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {formatDuration(existingRecordingState.duration)}
        </span>
        {existingRecordingState.segmentCount > 0 && (
          <span className="flex items-center gap-1">
            <Layers className="h-3.5 w-3.5" />
            {existingRecordingState.segmentCount} segment{existingRecordingState.segmentCount !== 1 ? 's' : ''}
          </span>
        )}
        {renderStatusBadge()}
      </div>
    );
  };

  /** Transcript section with edit/save/copy */
  const renderTranscriptSection = () => {
    if (!transcript) return null;

    return (
      <div className="space-y-2">
        <button
          onClick={() => setShowTranscript(!showTranscript)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700"
        >
          {showTranscript ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Transcript
        </button>

        {showTranscript && (
          <div className="space-y-2">
            {isEditingTranscript ? (
              <textarea
                value={editedTranscript}
                onChange={(e) => setEditedTranscript(e.target.value)}
                className="w-full h-32 p-3 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{transcript}</p>
              </div>
            )}

            <div className="flex items-center gap-2">
              {isEditingTranscript ? (
                <>
                  <Button size="sm" variant="outline" onClick={handleSaveTranscript} disabled={savingTranscript}>
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                    {savingTranscript ? 'Saving...' : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelTranscriptEdit}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={() => setIsEditingTranscript(true)}>
                    <Edit3 className="h-3.5 w-3.5 mr-1.5" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCopyTranscript}>
                    <Copy className="h-3.5 w-3.5 mr-1.5" />
                    Copy
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============ Render ============

  return (
    <div className="fixed bottom-16 sm:bottom-24 right-3 sm:right-6 left-3 sm:left-auto w-auto sm:w-96 max-h-[70vh] sm:max-h-[600px] bg-white rounded-lg shadow-2xl z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-medium text-gray-900">Scribe</h3>
        <div className="flex items-center gap-2">
          {wakeLockActive && (
            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Screen on</span>
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Patient info */}
      {currentPatient && (
        <div className="px-4 py-2 bg-gray-50 border-b">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Patient:</span>
            <span className="font-medium text-gray-900">
              {formatPatientName(currentPatient, nameFormat)}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* ── Template Selection (always shown when not recording) ── */}
        {!activeRecording && (
          <div ref={templateDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Documentation Template
            </label>
            <div className="relative">
              <button
                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <span className={selectedTemplate ? 'text-gray-900' : 'text-gray-500'}>
                  {selectedTemplate?.name || 'Select a template...'}
                </span>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showTemplateDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {/* SOAP option */}
                  <button
                    onClick={() => handleTemplateSelect(SOAP_OPTION)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between border-b"
                  >
                    <div>
                      <div className="font-medium text-gray-900">Standard SOAP Note</div>
                      <div className="text-sm text-gray-500">Classic format</div>
                    </div>
                    {selectedTemplate?.id === '__soap__' && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </button>

                  {isLoadingTemplates ? (
                    <div className="px-4 py-3 text-gray-500 text-sm flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading templates...
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="px-4 py-3 text-gray-500 text-sm">No templates available</div>
                  ) : (
                    templates.map((tmpl) => (
                      <button
                        key={tmpl.id}
                        onClick={() => handleTemplateSelect(tmpl)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium text-gray-900">{tmpl.name}</div>
                          {tmpl.note_type && (
                            <div className="text-sm text-gray-500">{tmpl.note_type}</div>
                          )}
                        </div>
                        {selectedTemplate?.id === tmpl.id && (
                          <Check className="w-5 h-5 text-primary" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {templateError && (
              <p className="text-xs text-red-500 mt-1">{templateError}</p>
            )}

            {/* Info message for template mode without encounter */}
            {isTemplateMode && !activeNoteContext?.encounterId && !recordingConflict && (
              <p className="text-xs text-amber-600 mt-2">
                Open a clinical note to use template mode recording.
              </p>
            )}

            {/* Conflict: recording active on another note */}
            {recordingConflict && (
              <p className="text-xs text-amber-600 mt-2">
                A recording is in progress for another note — stop it before recording here.
              </p>
            )}
          </div>
        )}

        {/* ── Recording Info (when audio exists and not actively recording) ── */}
        {!activeRecording && hasAudio && renderRecordingInfo()}

        {/* ── Active Recording ── */}
        {activeRecording && (
          <>
            {selectedTemplate && (
              <div className="text-sm text-gray-500 text-center">
                Template: <span className="font-medium text-gray-700">{selectedTemplate.name}</span>
              </div>
            )}
            <RecordingControls
              isRecording={true}
              isPaused={isPaused}
              duration={activeDuration}
              onStart={handleStartRecording}
              onPause={pauseRecording}
              onResume={resumeRecording}
              onStop={handleStopRecording}
            />
          </>
        )}

        {/* ── Recording Controls (when not recording) ── */}
        {!activeRecording && (
          <div className="space-y-3">
            {/* Start / Add to Recording button */}
            {!locked && (
              <RecordingControls
                isRecording={false}
                isPaused={false}
                duration={0}
                onStart={handleStartRecording}
                onPause={pauseRecording}
                onResume={resumeRecording}
                onStop={handleStopRecording}
                disabled={!selectedTemplate || (isTemplateMode && !activeNoteContext?.encounterId) || recordingConflict}
                buttonText={
                  recordingConflict
                    ? 'Recording active on another note'
                    : !selectedTemplate
                      ? 'Select a template first'
                      : isTemplateMode && !activeNoteContext?.encounterId
                        ? 'Open a clinical note first'
                        : getRecordButtonText()
                }
              />
            )}

            {/* Locked state message */}
            {locked && (
              <div className="text-center py-2">
                <p className="text-sm text-gray-500">
                  Recording complete — transcription has been submitted
                </p>
              </div>
            )}

            {/* Failed transcription — surface the reason; Transcribe becomes Retry */}
            {existingRecordingState?.transcriptionStatus === 'failed' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  Transcription failed{existingRecordingState?.transcriptionError ? `: ${existingRecordingState.transcriptionError}` : ''}. Your audio is saved — you can retry.
                </p>
              </div>
            )}

            {/* Transcribe button */}
            {showTranscribeButton && (
              <Button
                onClick={handleTranscribe}
                className="w-full"
                disabled={!selectedTemplate || (isTemplateMode && !activeNoteContext?.encounterId)}
              >
                <FileText className="h-4 w-4 mr-2" />
                {existingRecordingState?.transcriptionStatus === 'failed' ? 'Retry Transcription' : 'Transcribe'}
              </Button>
            )}
          </div>
        )}

        {/* ── Transcription / Processing Status ── */}
        {isTranscribing && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            <span className="text-sm text-blue-700">Transcribing audio...</span>
          </div>
        )}

        {transcriptionError && (
          <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Transcription failed</p>
              <p className="text-xs text-red-600 mt-1">{transcriptionError}</p>
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            <span className="text-sm text-blue-700">
              {processingStatus === 'transcribing' ? 'Transcribing audio...' : 'Processing your recording...'}
            </span>
          </div>
        )}

        {isGeneratingNote && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            <span className="text-sm text-blue-700">Generating SOAP note...</span>
          </div>
        )}

        {/* ── Transcript Section ── */}
        {renderTranscriptSection()}

        {/* ── SOAP Generate button (after transcript is available) ── */}
        {isSoapMode && !hasResults && !isGeneratingNote && !isProcessing && !isTranscribing && transcript && (
          <Button onClick={handleGenerate} className="w-full">
            <FileText className="h-4 w-4 mr-2" />
            Generate SOAP Note
          </Button>
        )}

        {/* ── Template-mode results ── */}
        {isTemplateMode && generatedSectionsData && (
          <div className="space-y-2">
            <button
              onClick={() => setShowGeneratedNote(!showGeneratedNote)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700"
            >
              {showGeneratedNote ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Generated Fields
            </button>

            {showGeneratedNote && (
              <div className="space-y-3">
                {generatedSectionsData.sections?.map((section) => (
                  <div key={section.sectionId} className="space-y-2">
                    <h4 className="text-xs font-medium text-gray-600 uppercase">{section.sectionName}</h4>
                    {section.fields?.map((field) => (
                      <div key={field.fieldId} className="p-2 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-0.5">{field.fieldId}</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {typeof field.value === 'string' ? field.value : JSON.stringify(field.value, null, 2)}
                        </p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Billing & compliance review (template mode, open note) ── */}
        {isTemplateMode && activeNoteContext?.noteId && (
          <NoteReviewCard
            key={activeNoteContext.noteId}
            clinicalNoteId={activeNoteContext.noteId}
            onApplyCodes={handleApplyCodes}
          />
        )}

        {/* ── SOAP-mode results ── */}
        {!isTemplateMode && generatedNote && (
          <div className="space-y-2">
            <button
              onClick={() => setShowGeneratedNote(!showGeneratedNote)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700"
            >
              {showGeneratedNote ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Generated SOAP Note
            </button>

            {showGeneratedNote && (
              <div className="space-y-3">
                {['subjective', 'objective', 'assessment', 'plan'].map((section) => (
                  <div key={section} className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="text-xs font-medium text-gray-600 uppercase mb-1">{section}</h4>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {generatedNote[section]?.content || `No ${section} data`}
                    </p>
                  </div>
                ))}

                {generatedNote.icd10_suggestions?.length > 0 && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <h4 className="text-xs font-medium text-blue-700 uppercase mb-2">Suggested ICD-10 Codes</h4>
                    <div className="space-y-1">
                      {generatedNote.icd10_suggestions.map((code, index) => (
                        <div key={index} className="text-sm text-blue-600">
                          {code.code} - {code.description}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Generation error */}
        {noteGenerationError && (
          <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Generation failed</p>
              <p className="text-xs text-red-600 mt-1">{noteGenerationError}</p>
            </div>
          </div>
        )}

        {/* Empty state — no template selected */}
        {!activeRecording && !hasAudio && !selectedTemplate && (
          <div className="text-center py-4">
            <Mic className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-600">
              Select a template, then start recording
            </p>
          </div>
        )}
      </div>

      {/* Footer — Insert into Note (SOAP mode only; template mode uses realtime) */}
      {isSoapMode && hasResults && canInsertNote && (
        <div className="p-4 border-t">
          <Button onClick={handleInsertIntoNote} className="w-full">
            <FileText className="h-4 w-4 mr-2" />
            Insert into Clinical Note
          </Button>
        </div>
      )}
    </div>
  );
}
